import { useCallback, useEffect, useState } from "react";

/** iOS/iPadOS/iPhone — הפלטפורמה היחידה שבה beforeinstallprompt אף פעם
 *  לא יורה, ולכן צריך הנחיה ידנית ("שיתוף ← הוסף למסך הבית"). */
export function isIOSDevice(userAgent) {
  return /iPad|iPhone|iPod/.test(userAgent) && !/MSStream/.test(userAgent);
}

/** true כשהאפליקציה כבר רצה כמותקנת (נפתחה מהאייקון, לא מהדפדפן). */
export function isStandalone(matchMedia) {
  try { return !!matchMedia && matchMedia("(display-mode: standalone)").matches; }
  catch { return false; }
}

/* ============================================================================
   כרום/Edge יורים את beforeinstallprompt פעם אחת בלבד, כמה שניות אחרי
   הטעינה הראשונה של הדף — ולא יורים אותו שוב למאזין שנרשם מאוחר יותר.
   אם הלכידה הייתה תלויה ב-useEffect של useInstallPrompt, קומפוננטה
   שמתרנדרת רק מאוחר יותר (MorePage נטענת רק כשנכנסים ללשונית "עוד",
   TenantPortal רק אחרי שהזיהוי באזור הדייר הסתיים) הייתה מפספסת את
   האירוע לצמיתות — הכפתור פשוט לא היה מופיע לרוב המשתמשים (שאינם iOS).

   לכן המאזין נרשם כאן, בהיקף המודול, ברגע שהמודול נטען — לא בתוך אף
   קומפוננטה. main.jsx מייבא (סטטית) את Root.jsx שמייבא את App.jsx
   שמייבא את הקובץ הזה, ולכן הרישום קורה תוך כדי הערכת שרשרת ה-import,
   עוד לפני ש-main.jsx מגיע ל-createRoot(...).render(...). כלומר המאזין
   קיים הרבה לפני שנוצר רינדור ראשון כלשהו, לא רק לפני שמישהו פותח את
   "עוד". אותו טיפול ל-appinstalled: גם הוא עלול לירות לפני שאף
   קומפוננטה שמשתמשת ב-hook הזה בכלל נטענה.

   התבנית (משתנה מוטבע במודול + Set של מאזינים + notify) זהה לזו של
   vault (ראו keyvault.js: listeners/notify/onVaultChange) — לא ספריית
   ניהול מצב, רק המנגנון הקיים כבר בקוד הזה. */
let deferredEvent = null;
let installedFlag = typeof window !== "undefined"
  ? isStandalone(window.matchMedia?.bind(window))
  : false;
const listeners = new Set();
const notify = () => listeners.forEach((fn) => fn());

if (typeof window !== "undefined") {
  window.addEventListener("beforeinstallprompt", (e) => {
    e.preventDefault();
    deferredEvent = e;
    notify();
  });
  window.addEventListener("appinstalled", () => {
    installedFlag = true;
    deferredEvent = null;
    notify();
  });
}

/** חושף אם אפשר להציע התקנה (Android/Desktop, אחרי שהדפדפן ירה
 *  beforeinstallprompt), אם המכשיר הוא iOS (צריך הנחיה ידנית — ראו
 *  IOSInstallHelp ב-App.jsx), ואם האפליקציה כבר מותקנת.
 *
 *  המצב עצמו נלכד בהיקף המודול (למעלה) ולא כאן — ה-hook רק נרשם
 *  לעדכונים ומתרנדר מחדש כשהם מגיעים, כדי לא לפספס אירוע שכבר ירה
 *  לפני שהקומפוננטה הראשונה בכלל נטענה. */
export function useInstallPrompt() {
  const [, setTick] = useState(0);

  useEffect(() => {
    const onChange = () => setTick((n) => n + 1);
    listeners.add(onChange);
    return () => listeners.delete(onChange);
  }, []);

  const promptInstall = useCallback(async () => {
    if (!deferredEvent) return;
    const evt = deferredEvent;
    evt.prompt();
    await evt.userChoice;
    deferredEvent = null;
    notify();
  }, []);

  return {
    canInstall: !!deferredEvent && !installedFlag,
    isIOS: isIOSDevice(typeof navigator !== "undefined" ? navigator.userAgent : ""),
    isInstalled: installedFlag,
    promptInstall,
  };
}
